import { useMemo, useState } from 'react';
import type { EnrichedFeature } from '@/hooks/useGeoLayer';
import {
  countExportableFeatures,
  exportFeaturesToGeoJsonFile,
  fetchFreshGeoLayerFeatures,
} from '@/lib/geojson-export';
import { getLayerDisplayName } from './layer-options';
import styles from './SyncControlPanel.module.css';

interface GeoJsonExportBarProps {
  layerId: string;
  features: EnrichedFeature[];
  mapLoading?: boolean;
}

/** GeoJSON download only — no ArcGIS sync UI (AgriTour public map). */
export function GeoJsonExportBar({
  layerId,
  features,
  mapLoading = false,
}: GeoJsonExportBarProps) {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportableCount = useMemo(() => countExportableFeatures(features), [features]);

  async function handleExportGeoJson() {
    setNotice(null);
    setError(null);

    let exportFeatures = features;
    try {
      exportFeatures = await fetchFreshGeoLayerFeatures(layerId);
    } catch (err) {
      setError((err as Error).message);
      return;
    }

    const { filenames, stats } = await exportFeaturesToGeoJsonFile(exportFeatures, {
      layerId,
      layerDisplayName: getLayerDisplayName(layerId),
    });

    if (stats.featureCount === 0) {
      setError('אין פריטים עם מיקום לייצוא — ודא שהמפה נטענה.');
      return;
    }

    if (stats.skippedDrawnWithoutPolygon > 0) {
      setError(
        `יוצאו ${stats.featureCount} פריטים (${stats.polygonCount + stats.multiPolygonCount} מצוירים, ${stats.pointCount} נקודות). ${stats.skippedDrawnWithoutPolygon} מצוירים ללא פוליגון.`,
      );
      return;
    }

    const polygonTotal = stats.polygonCount + stats.multiPolygonCount;
    if (filenames.length > 1 && polygonTotal > 0) {
      setNotice(
        `הורדתם 2 קבצים: parcels (${polygonTotal} מגרשים) ו-sites (${stats.pointCount} נקודות).`,
      );
      return;
    }

    if (polygonTotal > 0) {
      setNotice(`הורד קובץ parcels עם ${polygonTotal} מגרשים.`);
    } else {
      setNotice(`הורד קובץ עם ${stats.pointCount} נקודות.`);
    }
  }

  return (
    <section className={styles.root} dir="rtl" aria-label="ייצוא GeoJSON">
      <div className={styles.summary}>
        <div className={styles.summaryStart}>
          <div className={styles.summaryText}>
            <span className={styles.summaryTitle}>ייצוא נתונים</span>
            <span className={styles.summaryHint}>
              {exportableCount > 0
                ? `${exportableCount} פריטים עם מיקום זמינים להורדה`
                : 'אין פריטים עם מיקום לייצוא'}
            </span>
          </div>
        </div>
        <div className={styles.summaryActions}>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={() => void handleExportGeoJson()}
            disabled={mapLoading || exportableCount === 0}
            title="הורדת GeoJSON"
          >
            ייצוא GeoJSON
          </button>
        </div>
      </div>
      {error ? <p className={styles.bannerError} role="alert">{error}</p> : null}
      {notice && !error ? <p className={styles.bannerInfo} role="status">{notice}</p> : null}
    </section>
  );
}
